import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { BehaviorSubject, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  ArrowLeft,
  Copy,
  Check,
  FileText,
  GraduationCap,
  LucideAngularModule,
  Share2,
} from 'lucide-angular';
import {
  AssignmentApiService,
  AssignmentResponse,
} from '../assignment/services/assignment-api.service';

@Component({
  selector: 'app-assignment-detail',
  standalone: true,
  imports: [CommonModule, RouterModule, LucideAngularModule],
  templateUrl: './assignment-detail.html',
  styleUrl: './assignment-detail.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignmentDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly apiService = inject(AssignmentApiService);

  protected readonly icons = {
    ArrowLeft,
    Copy,
    Check,
    FileText,
    GraduationCap,
    Share2,
  };

  private readonly assignment$$ =
    new BehaviorSubject<AssignmentResponse | null>(null);
  protected readonly assignment$ = this.assignment$$.asObservable();

  protected readonly copied = signal(false);

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) return;

    this.apiService
      .getAssignment(id)
      .pipe(catchError(() => of(null)))
      .subscribe((assignment) => this.assignment$$.next(assignment));
  }

  protected getTitle(assignment: AssignmentResponse): string {
    return (
      assignment.title?.trim() ||
      assignment.objective?.trim() ||
      'Untitled assignment'
    );
  }

  protected getShareLink(assignment: AssignmentResponse): string {
    if (!assignment.shareCode) return '';
    const baseUrl =
      typeof window !== 'undefined' ? window.location.origin : '';
    return `${baseUrl}/a/${assignment.shareCode}`;
  }

  protected async copyShareLink(assignment: AssignmentResponse): Promise<void> {
    const link = this.getShareLink(assignment);
    if (!link) return;

    try {
      await navigator.clipboard.writeText(link);
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 2000);
    } catch {
      // Fallback: do nothing
    }
  }

  protected goBack(): void {
    this.router.navigate(['/teacher/dashboard/assignments']);
  }
}
